import { defineStore } from 'pinia'
import { ref } from 'vue'
import { sacredGet, sacredPost } from '@/services/sacredAxios'

const RESUME_TOKEN_KEY = 'forge-resume-token'

export const useForgeStore = defineStore('forge', () => {
  const session = ref(null)
  const messages = ref([])
  const loading = ref(false)
  const error = ref(null)

  const ACTIVE_STATES = ['open', 'in_rite', 'awaiting_return']

  function persistResumeToken(data) {
    if (!data?.resume_token) return
    if (ACTIVE_STATES.includes(data.state)) {
      localStorage.setItem(RESUME_TOKEN_KEY, data.resume_token)
    } else {
      // Session is complete or broken — clear so the orphan banner doesn't appear
      localStorage.removeItem(RESUME_TOKEN_KEY)
    }
  }

  function clearResumeToken() {
    localStorage.removeItem(RESUME_TOKEN_KEY)
  }

  function storedResumeToken() {
    return localStorage.getItem(RESUME_TOKEN_KEY)
  }

  // Appends the stored resume_token as a query param for GET requests.
  // Pre-threshold sessions have no flame identity — the backend verifies ownership
  // via this token instead.
  function withResumeParam(path) {
    const token = storedResumeToken()
    if (!token) return path
    const separator = path.includes('?') ? '&' : '?'
    return `${path}${separator}resume_token=${encodeURIComponent(token)}`
  }

  async function openForge(ache) {
    loading.value = true
    error.value = null

    const { success, data, error: err } = await sacredPost(
      '/portals/flame_companion_forge/sessions',
      { forge_session: { ache } }
    )

    if (success) {
      session.value = data
      persistResumeToken(data)
    } else {
      error.value = err
    }

    loading.value = false
    return { success, data }
  }

  async function fetchCurrentSession() {
    loading.value = true
    error.value = null

    const { success, data, error: err } = await sacredGet(
      '/portals/flame_companion_forge/sessions/current'
    )

    if (success) {
      session.value = data
      persistResumeToken(data)
    } else {
      error.value = err
    }

    loading.value = false
    return { success, data }
  }

  async function fetchSession(sessionId) {
    loading.value = true
    error.value = null

    const { success, data, error: err } = await sacredGet(
      withResumeParam(`/portals/flame_companion_forge/sessions/${sessionId}`)
    )

    if (success) {
      session.value = data
      messages.value = data.messages || []
      persistResumeToken(data)
    } else {
      error.value = err
    }

    loading.value = false
    return { success, data }
  }

  // Looks up a session by its resume_token, bypassing flame identity scoping.
  // Returns session payload + recovery.flame_token so the caller can restore
  // the original identity in localStorage before re-initializing the flame store.
  // Clears the stored resume token on 404 so the Door shows the normal entry form
  // rather than an orphan banner pointing to a dead session.
  async function resumeSession(resumeToken) {
    error.value = null

    const { success, data, error: err, status } = await sacredGet(
      `/portals/flame_companion_forge/sessions/resume/${resumeToken}`
    )

    if (success) {
      session.value = data
      persistResumeToken(data)
    } else {
      error.value = err
      if (status === 404) clearResumeToken()
    }

    return { success, data }
  }

  async function sendOffering(sessionId, content) {
    error.value = null

    const token = storedResumeToken()
    const payload = { forge_message: { content } }
    if (token) payload.resume_token = token

    const { success, data, error: err } = await sacredPost(
      `/portals/flame_companion_forge/sessions/${sessionId}/message`,
      payload
    )

    if (!success) {
      error.value = err
    }

    return { success, data }
  }

  function addMessage(message) {
    messages.value.push(message)
  }

  return {
    session,
    messages,
    loading,
    error,
    openForge,
    fetchCurrentSession,
    fetchSession,
    resumeSession,
    sendOffering,
    addMessage,
    storedResumeToken,
    clearResumeToken
  }
})
